/* ============================================================
   /app/scan — M15 frontend: camera capture -> OCR review -> confirm.

   [capture] -> POST /api/scan (multipart) -> review screen, every
   field editable -> [confirm] -> POST /api/scan/confirm -> lead saved.
   No lead is ever written from raw model output; the review screen is
   mandatory (mirrors the backend's own design — see scan.py).
   ============================================================ */
import { type FormEvent, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { HttpError } from "../../lib/api";
import { authedFetch, isLoggedIn } from "../../lib/auth";

type ScanResult = {
  full_name: string | null;
  title: string | null;
  company: string | null;
  mobile: string | null;
  landline: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  linkedin_url: string | null;
  website_url: string | null;
  confidence: "high" | "medium" | "low";
  card_image_key: string | null;
};

type FormState = {
  full_name: string;
  title: string;
  company: string;
  mobile: string;
  landline: string;
  email: string;
  address: string;
  city: string;
  linkedin_url: string;
  website_url: string;
};

// OCR frequently returns bare domains ("www.acme.com") with no scheme.
// The browser's native `type="url"` validation rejects those on submit
// with a blocking "Enter a URL" prompt — and a bare domain saved as-is
// would also render as a broken relative link anywhere it's used as an
// href later (e.g. the admin leads table). Default to https://.
function normalizeUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function toForm(result: ScanResult): FormState {
  return {
    full_name: result.full_name ?? "",
    title: result.title ?? "",
    company: result.company ?? "",
    mobile: result.mobile ?? "",
    landline: result.landline ?? "",
    email: result.email ?? "",
    address: result.address ?? "",
    city: result.city ?? "",
    linkedin_url: normalizeUrl(result.linkedin_url ?? ""),
    website_url: normalizeUrl(result.website_url ?? ""),
  };
}

type State =
  | { kind: "unauthenticated" }
  | { kind: "capture" }
  | { kind: "scanning" }
  | {
      kind: "review";
      form: FormState;
      confidence: ScanResult["confidence"];
      cardImageKey: string | null;
    }
  | {
      kind: "confirming";
      form: FormState;
      confidence: ScanResult["confidence"];
      cardImageKey: string | null;
    }
  | { kind: "done"; emailStatus: string }
  | { kind: "error"; message: string };

export default function AppScan() {
  const [state, setState] = useState<State>(
    isLoggedIn() ? { kind: "capture" } : { kind: "unauthenticated" },
  );
  const [consent, setConsent] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function onFileSelected(file: File) {
    setState({ kind: "scanning" });
    try {
      const body = new FormData();
      body.append("file", file);
      const resp = await authedFetch("/api/scan", { method: "POST", body });
      if (!resp.ok) {
        const detail = await safeDetail(resp);
        throw new HttpError(resp.status, detail);
      }
      const result = (await resp.json()) as ScanResult;
      setConsent(true);
      setSendEmail(false);
      setState({
        kind: "review",
        form: toForm(result),
        confidence: result.confidence,
        cardImageKey: result.card_image_key,
      });
    } catch (e: unknown) {
      setState({ kind: "error", message: errorMessage(e, "Scan failed. Try again.") });
    }
  }

  async function onConfirm(e: FormEvent) {
    e.preventDefault();
    if (state.kind !== "review") return;
    setState({
      kind: "confirming",
      form: state.form,
      confidence: state.confidence,
      cardImageKey: state.cardImageKey,
    });
    try {
      const resp = await authedFetch("/api/scan/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: state.form.full_name.trim() || null,
          title: state.form.title.trim() || null,
          company: state.form.company.trim() || null,
          mobile: state.form.mobile.trim() || null,
          landline: state.form.landline.trim() || null,
          email: state.form.email.trim() || null,
          address: state.form.address.trim() || null,
          city: state.form.city.trim() || null,
          linkedin_url: state.form.linkedin_url.trim() || null,
          website_url: state.form.website_url.trim() || null,
          card_image_key: state.cardImageKey,
          consent,
          send_email: sendEmail && consent,
        }),
      });
      if (!resp.ok) {
        const detail = await safeDetail(resp);
        throw new HttpError(resp.status, detail);
      }
      const result = (await resp.json()) as { lead_id: string; email_status: string };
      setState({ kind: "done", emailStatus: result.email_status });
    } catch (e: unknown) {
      setState({
        kind: "error",
        message: errorMessage(e, "Couldn't save this contact. Try again."),
      });
    }
  }

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    if (state.kind !== "review") return;
    setState({ ...state, form: { ...state.form, [key]: value } });
  }

  function reset() {
    setState({ kind: "capture" });
  }

  if (state.kind === "unauthenticated") {
    return <Navigate to="/app/login" replace />;
  }

  return (
    <main className="min-h-screen px-6 pb-6 pt-safe bg-paper">
      <div className="max-w-md mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-xl font-bold text-ink">Scan a card</h1>
          <Link to="/app" className="text-xs text-ink/60 underline">
            ← My card
          </Link>
        </div>

        {(state.kind === "capture" || state.kind === "scanning") && (
          <div className="space-y-4">
            <p className="text-ink/60 text-sm">
              Take a photo of a business card. You'll be able to check and fix every field before
              anything is saved.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void onFileSelected(file);
              }}
            />
            <button
              type="button"
              disabled={state.kind === "scanning"}
              onClick={() => fileInputRef.current?.click()}
              className="w-full rounded-lg bg-ink text-paper font-semibold py-4 text-sm disabled:opacity-50"
            >
              {state.kind === "scanning" ? "Reading card…" : "Take photo"}
            </button>
          </div>
        )}

        {(state.kind === "review" || state.kind === "confirming") && (
          <form onSubmit={onConfirm} className="space-y-4">
            {state.confidence !== "high" && (
              <p className="text-xs rounded-lg bg-yellow-50 text-yellow-800 px-3 py-2">
                {state.confidence === "low"
                  ? "Low-confidence read — please double-check every field."
                  : "Medium-confidence read — please check the details below."}
              </p>
            )}

            <Field label="Full name">
              <input
                type="text"
                value={state.form.full_name}
                onChange={(e) => set("full_name", e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Title">
              <input
                type="text"
                value={state.form.title}
                onChange={(e) => set("title", e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Company">
              <input
                type="text"
                value={state.form.company}
                onChange={(e) => set("company", e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Mobile">
              <input
                type="tel"
                value={state.form.mobile}
                onChange={(e) => set("mobile", e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Landline">
              <input
                type="tel"
                value={state.form.landline}
                onChange={(e) => set("landline", e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={state.form.email}
                onChange={(e) => set("email", e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Address">
              <input
                type="text"
                value={state.form.address}
                onChange={(e) => set("address", e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="City">
              <input
                type="text"
                value={state.form.city}
                onChange={(e) => set("city", e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="LinkedIn URL">
              <input
                type="url"
                value={state.form.linkedin_url}
                onChange={(e) => set("linkedin_url", e.target.value)}
                onBlur={(e) => set("linkedin_url", normalizeUrl(e.target.value))}
                className={inputClass}
              />
            </Field>
            <Field label="Website URL">
              <input
                type="url"
                value={state.form.website_url}
                onChange={(e) => set("website_url", e.target.value)}
                onBlur={(e) => set("website_url", normalizeUrl(e.target.value))}
                className={inputClass}
              />
            </Field>

            <label className="flex items-start gap-2 text-xs text-ink/70">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5"
              />
              <span>I have this person's consent to store their details as a contact.</span>
            </label>

            {state.form.email.trim() !== "" && (
              <label className="flex items-start gap-2 text-xs text-ink/70">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  disabled={!consent}
                  className="mt-0.5"
                />
                <span>Also email them my card ({state.form.email.trim()}).</span>
              </label>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={reset}
                disabled={state.kind === "confirming"}
                className="flex-1 rounded-lg border border-ink/15 text-ink font-semibold py-3 text-sm disabled:opacity-50"
              >
                Rescan
              </button>
              <button
                type="submit"
                disabled={!consent || state.kind === "confirming"}
                className="flex-1 rounded-lg bg-ink text-paper font-semibold py-3 text-sm disabled:opacity-50"
              >
                {state.kind === "confirming" ? "Saving…" : "Save contact"}
              </button>
            </div>
          </form>
        )}

        {state.kind === "done" && (
          <div className="text-center space-y-4 py-8">
            <p className="text-signal text-sm font-semibold">Contact saved.</p>
            {state.emailStatus === "sent" && (
              <p className="text-ink/60 text-xs">Your card was emailed to them.</p>
            )}
            {state.emailStatus === "failed" && (
              <p className="text-red-600 text-xs">
                Contact saved, but the email couldn't be sent.
              </p>
            )}
            <button
              type="button"
              onClick={reset}
              className="w-full rounded-lg bg-ink text-paper font-semibold py-3 text-sm"
            >
              Scan another card
            </button>
          </div>
        )}

        {state.kind === "error" && (
          <div className="text-center space-y-4 py-8">
            <p className="text-red-600 text-sm">{state.message}</p>
            <button
              type="button"
              onClick={reset}
              className="w-full rounded-lg bg-ink text-paper font-semibold py-3 text-sm"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </main>
  );
}

async function safeDetail(resp: Response): Promise<string> {
  try {
    const data = (await resp.json()) as { detail?: string };
    return data.detail ?? `HTTP ${resp.status}`;
  } catch {
    return `HTTP ${resp.status}`;
  }
}

function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof HttpError) {
    if (e.status === 401) return "Session expired — please sign in again.";
    if (e.status === 429) return "Daily scan limit reached — try again tomorrow.";
    return e.message || fallback;
  }
  return fallback;
}

const inputClass =
  "w-full rounded-lg border border-ink/15 bg-white px-4 py-3 text-sm text-ink " +
  "placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-signal";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink/60 mb-1">{label}</span>
      {children}
    </label>
  );
}
